import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import {
  getAllDocuments,
  getDocumentsByCaseFile,
  uploadDocument,
} from "../redux/actions/documentActions";
import { fetchFiles, getFile } from "../redux/actions/fileActions";
import { getMyFolders } from "../redux/actions/folderActions";
import {
  FormControl,
  Container,
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  CircularProgress,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,

} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import UploadIcon from "@mui/icons-material/Upload"; // Import the UploadIcon
import { useNavigate, useLocation } from "react-router-dom";

import FileAutocomplete from "./FileAutocomplete";

import { actionButtonStyles } from "../styles/actionButton";
import { textFieldStyles } from "../styles/textFieldStyles";
import BreadcrumbNav from "./BreadcrumbNav";
const buttonStyles = {
  borderRadius: 1, // Rounded corners
  padding: "6px 16px", // Custom padding
  textTransform: "none", // Disable uppercase text
  boxShadow: "none", // Remove default shadow
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)", // Add custom shadow on hover
  },
};

const chooseFileButtonStyles = {
  color: "#007bff",
  borderColor: "#007bff",
  "&:hover": { backgroundColor: "#007bff1a" },
};

const uploadButtonStyles = {
  backgroundColor: "#2196f3", // Blue background
  color: "#fff", // White text
  "&:hover": {
    backgroundColor: "#1976d2", // Darker blue on hover
  },
};
const DocumentUpload = () => {
  const { t, i18n } = useTranslation(['pages', 'forms', 'buttons', 'common', 'messages']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [owner, setOwner] = useState("");
  const location = useLocation();
  // console.log("location", location.state);

  const { loading, success, error } = useSelector(
    (state) => state.documentUpload
  );

  const { user } = useSelector((state) => state.userLoaded);
  const { files: caseFiles } = useSelector((state) => state.fileList);
  const { folders: userFolders, loading: foldersLoading, error: foldersError } = useSelector((state) => state.folderByOwner);
  const currentLanguage = useSelector((state) => state.language?.currentLanguage);
  // Filter files based on user role
  const filteredFiles = (caseFiles || []).filter((file) => {
    if (user?.role === "Manager") {
      return true; // Managers can see all files
    } else if (user?.role === "Responsible Lawyer") {
      return file.officeMembers.some((member) => {
        return (
          member.member._id === user?.relationship &&
          member.role === "Responsible Lawyer"
        );
      });
    } else if (user?.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user?.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t('navigation:home'), href: "/" },
    { label: t('common:documents'), href: "/documents" },
  ]);

  // 1. Update initial state
  const [formData, setFormData] = useState(() => {
    const locationCaseId = location.state?.caseId;
    const initialCaseId = locationCaseId;
    const initialCaseFile = filteredFiles.find((file) => file._id === initialCaseId) || null;

    if (initialCaseFile) {
      setBreadcrumbs((prevBreadcrumbs) => [
        ...prevBreadcrumbs,
        {
          label: initialCaseFile.name || t('common:case_file'),
          href: `/files/${initialCaseFile._id}/documents`,
        },
      ]);
    }

    return {
      title: "",
      description: "",
      folder: "",
      uploadFiles: [], // Changed name to avoid conflict
      caseFile: initialCaseFile,
      type: "",
    };
  });

  const { title, description, folder, uploadFiles, caseFile, type } = formData;

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [titleEdited, setTitleEdited] = useState(false);

  // Force re-render when language changes
  useEffect(() => {
    // This effect will run when language changes, forcing a re-render
    // The component will re-render when either i18n.language or currentLanguage changes
  }, [i18n.language, currentLanguage]);

  useEffect(() => {
    if (user) {
      setOwner(user.relationship);
    }
  }, [user]);

  useEffect(() => {
    dispatch(fetchFiles());
  }, [dispatch]);

  // Fetch folders for current user
  useEffect(() => {
    if (user?.relationship) {
      dispatch(getMyFolders(currentLanguage || i18n.language, caseFile?._id || null));
    }
  }, [dispatch, currentLanguage, i18n.language, user?.relationship, caseFile]);



  useEffect(() => {
    if (success) {
      setNotification({
        open: true,
        message: t('messages:document_uploaded_successfully'),
        severity: "success",
      });
      dispatch({ type: "DOCUMENT_UPLOAD_RESET" }); // Reset the success state
      setTimeout(() => {
        // Check if we came from a case-specific context
        const caseId = location.state?.caseId;
        if (caseId) {
          // Redirect back to the case-specific documents tab
          navigate(`/files/${caseId}/documents`);
        } else {
          // Redirect to global documents page (existing behavior)
          navigate('/documents');
        }
      }, 1000);
    } else if (error) {
      setNotification({
        open: true,
        message: error,
        severity: "error",
      });
      dispatch({ type: "DOCUMENT_UPLOAD_RESET" }); // Reset the error state
    }
  }, [success, error, dispatch, navigate, location.state]);



  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const onChange = (e) => {
    if (e.target.name === "file") {
      const newSelectedFiles = Array.from(e.target.files);
      if (newSelectedFiles.length === 0) return;

      const combinedFiles = [...uploadFiles, ...newSelectedFiles];

      let newTitle = title;
      // Only auto-set the title if the user hasn't edited it manually
      if (!titleEdited && newSelectedFiles.length > 0) {
        newTitle = newSelectedFiles[0].name.split('.').slice(0, -1).join('.');
      }

      setFormData({
        ...formData,
        uploadFiles: combinedFiles,
        title: newTitle,
        type: 'multiple'
      });
    } else if (e.target.name === "title") {
      setTitleEdited(true);
      setFormData({ ...formData, [e.target.name]: e.target.value });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const onCaseFileChange = (_, value) => {
    setFormData({ ...formData, caseFile: value });
  };



  // 2. Update submit handler
  const onSubmit = async (e) => {
    e.preventDefault();

    if (!caseFile) {
      setNotification({
        open: true,
        message: t('messages:please_select_case_file'),
        severity: "error",
      });
      return;
    }

    if (uploadFiles.length === 0) {
      setNotification({
        open: true,
        message: t('messages:please_select_at_least_one_file'),
        severity: "error",
      });
      return;
    }

    try {
      // Create a FormData instance
      const formData = new FormData();
      
      // Add non-file fields
      formData.append("folder", folder || "");
      formData.append("caseFile", caseFile._id);
      formData.append("owner", owner);
      formData.append("description", description || "");

      // Log the files being uploaded
      console.log('Files to upload:', uploadFiles);

      // Add all files
      uploadFiles.forEach((file) => {
        formData.append("files", file);
        formData.append("titles", title); // send the same title for all
      });

      // Log FormData contents (for debugging)
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      // Dispatch upload action
      await dispatch(uploadDocument(formData));

      // Reset form
      setFormData({
        title: "",
        description: "",
        folder: "",
        uploadFiles: [],
        caseFile: null,
        type: "",
      });

    } catch (err) {
      console.error('Upload error:', err);
      setNotification({
        open: true,
        message: err.message || t('messages:error_uploading_documents'),
        severity: "error",
      });
    }
  };

  const removeFile = (index) => {
    const newFiles = [...uploadFiles];
    newFiles.splice(index, 1);
    
    if (newFiles.length === 0) {
      setTitleEdited(false);
      setFormData({ ...formData, uploadFiles: [], title: "", type: "" });
    } else {
      setFormData({ ...formData, uploadFiles: newFiles });
    }
  };

  return (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:documents.upload_title')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />

      <Box
        sx={{
          backgroundColor: "white",
          padding: "30px 24px",
          marginTop: "24px",
        }}
      >
        <form onSubmit={onSubmit}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="title"
              label={t('forms:labels.title')}
              name="title"
              value={title}
              onChange={onChange}
              sx={textFieldStyles({
                width: "40%",
              })}
              variant="outlined"
              size="small"
            />
            <TextField
              margin="normal"
              fullWidth
              id="description"
              label={t('forms:labels.description')}
              name="description"
              value={description}
              onChange={onChange}
              sx={textFieldStyles({
                width: "40%",
              })}
              variant="outlined"
              size="small"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="folder"
              label={t('forms:labels.folder')}
              name="folder"
              value={folder}
              onChange={onChange}
              select
              sx={textFieldStyles({
                width: "40%",
              })}
              variant="outlined"
              size="small"
              disabled={foldersLoading}
            >
              {foldersLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  {t('messages:loading_folders')}
                </MenuItem>
              ) : userFolders && userFolders.length > 0 ? (
                userFolders
                  .sort((a, b) => {
                    // Sort default folders first, then by sortOrder, then alphabetically
                    if (a.isDefault && !b.isDefault) return -1;
                    if (!a.isDefault && b.isDefault) return 1;
                    return (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name);
                  })
                  .map((folderItem) => {
                    // Translate default folder names
                    const getFolderDisplayName = (name) => {
                      const folderTranslations = {
                        'Correspondence': t('forms:folders.correspondence'),
                        'Medical': t('forms:folders.medical'),
                        'Financial': t('forms:folders.financial'),
                        'Other': t('forms:folders.other')
                      };
                      return folderTranslations[name] || name;
                    };

                    return (
                      <MenuItem key={folderItem._id} value={folderItem.name}>
                        {getFolderDisplayName(folderItem.name)}
                      </MenuItem>
                    );
                  })
              ) : (
                <MenuItem disabled>
                  {foldersError ?
                    `Error loading folders: ${foldersError}` :
                    t('messages:no_folders_available')
                  }
                </MenuItem>
              )}
            </TextField>
            <FormControl variant="standard" sx={{ width: "40%" }}>
              <FileAutocomplete
                options={filteredFiles}
                value={
                  filteredFiles.find(
                    (file) => file?._id === formData.caseFile?._id
                  ) || null
                }
                onChange={onCaseFileChange}
                label={t('common:case')}
                name="case"
              />
            </FormControl>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Button
                variant="text"
                component="label"
                startIcon={<AttachFileIcon />}
                sx={chooseFileButtonStyles}
              >
{t('buttons:secondary.choose_files')}
                <input 
                  type="file"
                  hidden
                  name="file"
                  onChange={onChange}
                  multiple
                  accept="*/*"
                />
              </Button>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {uploadFiles.map((file, index) => (
                  <Chip
                    key={index}
                    icon={<DescriptionIcon />}
                    label={file.name}
                    onDelete={() => removeFile(index)}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<UploadIcon />} // Add the UploadIcon here
              sx={actionButtonStyles()}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('buttons:primary.upload')}
            </Button>
          </Box>
        </form>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>


    </Container>
  );
};

export default DocumentUpload;
